import Phaser from 'phaser';
import { GameScene, type NPCRenderObjects } from '../GameScene';
import type { NPCStateView } from '../../state/GameStateView';
import { TILE_SIZE, FOOTSTEP_BASE_DURATION_MS } from '@shared/constants';
import { COMBAT_DEFAULTS } from '@shared/data/sounds';
import { Direction, EntityState } from '@shared/types/entity';
import { getNPCTemplate } from '@shared/data/npcs';
import { breathingPhaseOffset } from '../../util/hash';
import type { GameCursor } from '../../ui/cursors';
import { playAnimationAt } from '../../sprites/AnimationSpriteManager';
import { getNpcTypeIconName, getIconTexture } from '../../sprites/IconAtlases';
import {
  ensureBodyReady,
  getBodyAnimKey,
  getBodyCenterOffset,
  getBodyColumns,
  getBodyIdleFrame,
  getBodyLateralColumns,
  getBodySpriteKey,
  warnBadBodyAnim,
} from '../../sprites/BodySpriteManager';
import { getHeadFrameForDirection, getHeadSpriteKey } from '../../sprites/HeadSpriteManager';
import { getShieldIdleFrame, getShieldSpriteKey } from '../../sprites/ShieldSpriteManager';
import {
  getWeaponIdleFrame,
  getWeaponSheetOffsetForTextureKey,
  getWeaponSpriteKey,
} from '../../sprites/WeaponSpriteManager';
import { DEFAULT_PLAYER_FOOTSTEP_ID } from '../../sprites/FootstepSpriteManager';

/**
 * Sincronización de render de NPCs: crea/actualiza el conjunto de sprites de un NPC (body/head/
 * weapon/shield, animaciones, sombra, luz, label, depth, barco…) desde su NPCState (addOrUpdateNPC),
 * interpola sus posiciones por frame (updateNPCsPositions) y limpia al despawnear (removeNPC).
 * Extraído de GameScene (god-object). Todo el estado de render vive en GameScene (host) y se accede
 * vía `this.scene.*`. Hermano de PlayerSyncController. Ver docs/SPRITE_SYSTEM.md, docs/NPC_HOSTILE_AI.md.
 */
export class NpcSyncController {
  constructor(private scene: GameScene) {}

  public updateNPCsPositions(): void {
    const now = this.scene.time.now;
    
    this.scene.npcObjects.forEach((npc, npcId) => {
      // =====================================================================
      // TIME-BASED INTERPOLATION for NPCs (with optional easing)
      // Same formula as players: position = lerp(start, target, progress)
      // =====================================================================
      if (npc.moveDuration > 0) {
        const elapsed = now - npc.moveStartTime;
        const progress = Math.min(elapsed / npc.moveDuration, 1);
        
        // Lerp from start to target
        npc.currentWorldX = npc.startWorldX + (npc.targetWorldX - npc.startWorldX) * progress;
        npc.currentWorldY = npc.startWorldY + (npc.targetWorldY - npc.startWorldY) * progress;
        
        if (progress >= 1) {
          // Arrived at target
          npc.currentWorldX = npc.targetWorldX;
          npc.currentWorldY = npc.targetWorldY;
          npc.moveDuration = 0;
          npc.moveSpeed = 0;
        }
      }
      
      // ANTI-VIBRATION: Round positions to match player and camera behavior
      const worldX = Math.round(npc.currentWorldX);
      const worldY = Math.round(npc.currentWorldY);
      
      const bodyBottomY = worldY + GameScene.BODY_OFFSET_Y + GameScene.BODY_FRAME_HEIGHT / 2;
      const headCenterY = bodyBottomY - GameScene.HEAD_OFFSET_FROM_BODY_BOTTOM - GameScene.HEAD_FRAME_HEIGHT / 2;
      
      // =====================================================================
      // BREATHING EFFECT: NPCs always breathe (they're always "alive")
      // Same breathing effect as players: breathe for ~2s, then wait 5s, repeat
      // =====================================================================
      const BREATHING_DURATION = 4000; // ms que dura la animación de respiración (más lenta/profunda)
      const BREATHING_INTERVAL = 2500; // ms de espera entre respiraciones (el doble de frecuente)
      const BREATHING_CYCLE = BREATHING_DURATION + BREATHING_INTERVAL; // Ciclo completo
      const BREATHING_SCALE_AMPLITUDE = 0.015; // Variación de escala vertical (1.5% = muy sutil)
      
      // Fase con Date.now() (reloj compartido entre clientes) + offset DETERMINÍSTICO por id: todos
      // los clientes ven la MISMA animación para el mismo NPC, y NPCs distintos quedan escalonados.
      const cycleTime = (Date.now() + npc.breathingOffset) % BREATHING_CYCLE;
      let npcBreathingScaleY = 1.0;
      if (cycleTime < BREATHING_DURATION) {
        // Estamos en la fase de respiración: aplicar efecto seno
        const breathingProgress = cycleTime / BREATHING_DURATION; // 0 a 1
        npcBreathingScaleY = 1.0 + Math.sin(breathingProgress * Math.PI) * BREATHING_SCALE_AMPLITUDE;
      } else {
        // Estamos en la fase de espera: sin respiración
        npcBreathingScaleY = 1.0;
      }
      
      // Update all NPC visual positions. El body aplica su offset de centrado (los NPC bodies
      // anchos vienen ~1px corridos en el sheet; ver MANUAL_CENTER_OFFSETS).
      npc.sprite.setPosition(worldX + getBodyCenterOffset(npc.bodyId), bodyBottomY);
      npc.sprite.setScale(1.0, npcBreathingScaleY); // NPCs siempre respiran
      
      // Update NPC shadow position - projects from entity feet like clock hands
      if (npc.shadow) {
        const npcTileY = Math.floor(worldY / TILE_SIZE);
        const npcShadowDepth = this.scene.getEntityBaseDepthFromTileY(npcTileY) + GameScene.SHADOW_DEPTH_DELTA;
        
        // Shadow projects from NPC's feet position
        const maxDistance = 16;
        const shadowDistance = this.scene.shadowLength * maxDistance;
        
        const shadowOffsetX = Math.cos(this.scene.shadowAngle) * shadowDistance;
        const shadowOffsetY = Math.sin(this.scene.shadowAngle) * shadowDistance * 0.5;
        
        const shadowX = worldX + shadowOffsetX;
        const shadowY = bodyBottomY + shadowOffsetY + 2;
        
        const baseScale = 0.8;
        const stretchFactor = 1 + this.scene.shadowLength * 0.6;
        
        npc.shadow.setPosition(shadowX, shadowY);
        npc.shadow.setScale(baseScale * stretchFactor, baseScale * 0.6);
        npc.shadow.setAlpha(this.scene.shadowAlpha);
        npc.shadow.setDepth(npcShadowDepth);
      }

      // Fade de sombra: sin sombra bajo techo de casa (área `interior`), con ease suave.
      const npcShadowTarget = this.scene.mapAmbient.entityUnderRoof(worldX, bodyBottomY) ? 0 : 1;
      npc.shadowFade += (npcShadowTarget - npc.shadowFade) * GameScene.SHADOW_FADE_STEP;

      // Update NPC dropshadow position (independent system)
      // Si el NPC es el target seleccionado, ocultar su dropshadow para que no
      // compita visualmente con el retículo (ambos comparten textura/depth y
      // hacían z-fight al moverse).
      if (npc.dropshadow) {
        const isTargeted = this.scene.targets.selectedTargetId === npcId;
        npc.dropshadow.setVisible(!isTargeted && npc.shadowFade > 0.02);
        if (!isTargeted) {
          // Depth fijo bajo la banda de decoraciones: la sombra nunca pisa árboles.
          npc.dropshadow.setPosition(worldX, bodyBottomY + GameScene.DROPSHADOW_OFFSET_Y);
          npc.dropshadow.setDepth(GameScene.DROPSHADOW_GROUND_DEPTH);
          npc.dropshadow.setAlpha(GameScene.DROPSHADOW_ALPHA * npc.shadowFade);
        }
      }
      
      // Update NPC light position
      this.scene.entityLight.updateEntityLight(`npc_${npcId}`, worldX, bodyBottomY);
      
      this.scene.labels.setEntityLabelPosition(npc.label, worldX, bodyBottomY + 4);
      
      if (npc.tileZone) {
        npc.tileZone.setPosition(worldX, worldY);
      }
      
      if (npc.head) {
        npc.head.setPosition(worldX, headCenterY);
        npc.head.setScale(1.0, npcBreathingScaleY);
      }
      
      if (npc.shield) {
        npc.shield.setPosition(worldX + GameScene.SHIELD_OFFSET_X, bodyBottomY);
        npc.shield.setScale(1.0, npcBreathingScaleY);
      }
      
      if (npc.weapon) {
        // No hay var de dirección en este loop: la derivo del frame del arma
        // (filas r2=izq, r3=der son laterales) para aplicar el delta lateral.
        const wfn = npc.weapon.frame?.name;
        const lateralDx =
          typeof wfn === 'string' && (wfn.startsWith('r2') || wfn.startsWith('r3'))
            ? GameScene.WEAPON_LATERAL_DX
            : 0;
        const npcWeaponSheetOff = getWeaponSheetOffsetForTextureKey(npc.weapon.texture.key);
        npc.weapon.setPosition(
          worldX + GameScene.WEAPON_OFFSET_X + lateralDx + npcWeaponSheetOff.x,
          bodyBottomY + GameScene.WEAPON_OFFSET_Y + npcWeaponSheetOff.y
        );
        npc.weapon.setScale(1.0, npcBreathingScaleY);
      }
      
      // =====================================================================
      // DEPTH UPDATE: Update NPC depth every frame based on interpolated Y.
      // This ensures NPCs are sorted correctly with decorations and players.
      // =====================================================================
      const interpolatedTileY = Math.floor(worldY / TILE_SIZE);
      const npcBaseDepth = this.scene.getEntityBaseDepthFromTileY(interpolatedTileY);
      
      npc.sprite.setDepth(npcBaseDepth);
      if (npc.head) {
        npc.head.setDepth(npcBaseDepth + 0.01);
      }
      if (npc.shield) {
        npc.shield.setDepth(npcBaseDepth + 0.02);
      }
      if (npc.weapon) {
        npc.weapon.setDepth(npcBaseDepth + 0.02);
      }

      // Proyección de sombra: silueta inclinada según la hora (body+head+weapon+shield).
      // NPCs no tienen helm/boots como capas separadas (no existen esos campos).
      if (npc.castShadow) {
        this.scene.shadows.updateCastShadow(
          npc.castShadow,
          {
            body: npc.sprite,
            head: npc.head,
            weapon: npc.weapon,
            shield: npc.shield,
            helm: null,
            boots: null,
          },
          worldX,
          bodyBottomY,
          npc.shadowFade
        );
      }

      // Label siempre encima
      npc.label.setDepth(npcBaseDepth + GameScene.LABEL_DEPTH_DELTA);

      // Ícono de tipo: prefijo a la izquierda del nombre centrado, espejando
      // posición/alpha/visible del label (sin afectar el centrado del nombre).
      if (npc.typeIcon) {
        npc.typeIcon.setPosition(worldX - npc.label.width / 2 - 3, (bodyBottomY + 4) + npc.label.height / 2);
        npc.typeIcon.setAlpha(npc.label.alpha);
        npc.typeIcon.setVisible(npc.label.visible);
        npc.typeIcon.setDepth(npcBaseDepth + GameScene.LABEL_DEPTH_DELTA);
      }
    });
  }

  public addOrUpdateNPC(id: string, npc: NPCStateView): void {
    const existing = this.scene.npcObjects.get(id);

    // AO-like: Usar coordenadas de mundo directamente (la cámara se mueve sobre ellos)
    const worldX = npc.x * TILE_SIZE + TILE_SIZE / 2;
    const worldY = npc.y * TILE_SIZE + TILE_SIZE / 2;

    // Dirección y apariencia enviadas por el servidor (con defaults razonables)

    const direction = npc.direction ?? Direction.DOWN;

    const bodyId = (npc as any).bodyId !== undefined ? ((npc as any).bodyId as number) : 2;

    const headId = (npc as any).headId !== undefined ? ((npc as any).headId as number) : 0;

    const shieldId =

      (npc as any).shieldId !== undefined ? ((npc as any).shieldId as number) : 0;

    const weaponId =

      (npc as any).weaponId !== undefined ? ((npc as any).weaponId as number) : 0;

    const interact = (npc as any).interact !== undefined ? ((npc as any).interact as number) : 0;

    const message = (npc as any).message !== undefined ? ((npc as any).message as string) : '';

    const bodyY = worldY + GameScene.BODY_OFFSET_Y;

    const bodyBottomY = bodyY + GameScene.BODY_FRAME_HEIGHT / 2;

    const headCenterY =
      bodyBottomY -
      GameScene.HEAD_OFFSET_FROM_BODY_BOTTOM -
      GameScene.HEAD_FRAME_HEIGHT / 2;

    if (!existing) {
      // AO-like: Usar coordenadas de mundo directamente (la cámara se mueve sobre ellos)
      // NPC body: centrado en el tile SIN offset (origin 0.5, 1 para pivot en los pies).
      // AUTO-REPARACIÓN: cortar el body antes de crear el sprite (evita la lámina entera si el
      // warmup async aún no lo cortó; ver ensureBodyReady).
      ensureBodyReady(this.scene, bodyId);
      const sprite = this.scene.add.sprite(worldX, bodyBottomY, getBodySpriteKey(bodyId));
      sprite.setOrigin(0.5, 1);
      sprite.setFrame(getBodyIdleFrame(direction, bodyId));
      sprite.setScrollFactor(1); // Moverse con el mundo

      // Shadow depth for NPC
      const npcShadowDepth = this.scene.getEntityBaseDepthFromTileY(npc.y) + GameScene.SHADOW_DEPTH_DELTA;

      // Create simple elliptical shadow below NPC feet
      const shadowSprite = this.scene.entityLight.createShadowSprite(
        worldX,
        bodyBottomY,
        npcShadowDepth,
        1.0 // Scale multiplier
      );

      // Create dropshadow (independent system, scales with body width)
      const dropshadowSprite = this.scene.shadows.createDropshadowSprite(
        worldX,
        bodyBottomY,
        npcShadowDepth,
        bodyId
      );

      // Todo lo demás se centra en el tile (sin offset del body)
      const headSprite =
        headId > 0
          ? this.scene.add.sprite(worldX, headCenterY, getHeadSpriteKey(headId)).setOrigin(0.5, 0.5).setScrollFactor(1)
          : null;

      if (headSprite) {
        headSprite.setFrame(getHeadFrameForDirection(direction));
      }

      const shieldSprite =
        shieldId > 0
          ? this.scene.add
              .sprite(worldX + GameScene.SHIELD_OFFSET_X, bodyBottomY, getShieldSpriteKey(shieldId))
              .setOrigin(0.5, 1)
              .setScrollFactor(1)
          : null;

      if (shieldSprite) {
        shieldSprite.setFrame(getShieldIdleFrame(direction));
      }

      const weaponSprite =
        weaponId > 0
          ? this.scene.add
              .sprite(
                worldX +
                  GameScene.weaponOffsetX(direction) +
                  getWeaponSheetOffsetForTextureKey(getWeaponSpriteKey(weaponId)).x,
                bodyBottomY +
                  GameScene.WEAPON_OFFSET_Y +
                  getWeaponSheetOffsetForTextureKey(getWeaponSpriteKey(weaponId)).y,
                getWeaponSpriteKey(weaponId)
              )
              .setOrigin(0.5, 1)
              .setScrollFactor(1)
          : null;

      if (weaponSprite) {
        weaponSprite.setFrame(getWeaponIdleFrame(direction));
      }

      const npcBaseDepth = this.scene.getEntityBaseDepthFromTileY(npc.y);
      const labelDepth = npcBaseDepth + GameScene.LABEL_DEPTH_DELTA;

      // AO-like: Bajar 2px más el nombre (bodyBottomY + 2 + 2 = bodyBottomY + 4)
      // Mismo renderer que los players (addPlayerNameText): misma fuente
      // seleccionable, tamaño, bold y sombra — nombres parejos player/NPC.
      // Invocaciones propias: nombre en aguamarina para distinguirlas. Ver docs/SUMMONS.md.
      const isOwnSummon = !!(npc as NPCStateView).ownerId && (npc as NPCStateView).ownerId === this.scene.localSessionId;
      const npcLabelColor = isOwnSummon ? '#7fffd4' : '#ffffff';
      const label = this.scene.labels.addPlayerNameText(worldX, bodyBottomY + 4, npc.name || 'NPC', labelDepth, npcLabelColor);
      if (label) {
        label.setScrollFactor(1); // Moverse con el mundo
      }

      // Ícono del tipo de NPC (vendor/priest/spell-vendor) a la izquierda del
      // nombre. Espeja posición/alpha/visible del label en updateNPCsPositions.
      let typeIcon: Phaser.GameObjects.Sprite | null = null;
      const npcTemplateId = (npc as NPCStateView).templateId;
      const npcTmpl = npcTemplateId ? getNPCTemplate(npcTemplateId) : null;
      const npcTypeIconName = getNpcTypeIconName(npcTmpl);
      // Cursor según el tipo de NPC interactivo, por prioridad: ofrece quests → quest,
      // sacerdote → enchant, vendedor (de items o de hechizos) → shop, resto (NPC dialogable)
      // → talk. (Quest gana porque señala que el NPC tiene algo para vos.)
      const npcCursor: GameCursor = npcTmpl?.questIds?.length
        ? 'quest'
        : npcTmpl?.priestService
          ? 'enchant'
          : npcTmpl?.merchantId || npcTmpl?.spellVendorId
            ? 'shop'
            : 'talk';
      if (npcTypeIconName) {
        const npcTypeTex = getIconTexture(npcTypeIconName);
        if (npcTypeTex && this.scene.textures.exists(npcTypeTex.textureKey)) {
          typeIcon = this.scene.add
            .sprite(worldX, bodyBottomY + 4, npcTypeTex.textureKey, npcTypeTex.frame)
            .setOrigin(1, 0.5)
            .setDisplaySize(13, 13)
            .setDepth(labelDepth)
            .setScrollFactor(1)
            .setVisible(false)
            .setAlpha(0);
        }
      }

      // Visibilidad inicial: siempre oculto, se muestra en hover sobre el tile
      label.setAlpha(0);
      label.setVisible(false);

      // AO-like: Crear una zona invisible sobre el tile para detectar hover (usar coordenadas de mundo)
      // La zona debe estar por encima de otros elementos pero no bloquear clicks
      // Usamos una profundidad muy alta para que capture eventos por encima de sprites
      const tileZone = this.scene.add.zone(worldX, worldY, TILE_SIZE, TILE_SIZE)
        .setOrigin(0.5)
        .setDepth(labelDepth + 100) // Muy por encima del label para capturar eventos
        .setInteractive({ useHandCursor: false })
        .setScrollFactor(1); // Moverse con el mundo

      // Hover sobre el tile: sólo setea el flag de hover y el cursor. La visibilidad
      // del nombre la decide updateNpcNameVisibility() per-frame (única autoridad).
      tileZone.on('pointerover', () => {
        const r = this.scene.npcObjects.get(id);
        if (r) r.nameHovered = true;
        // Cursor por tipo de NPC: sólo si es interactivo y, estando muerto, sólo
        // sobre el sacerdote (isPriestNpc resuelve el template en vivo).
        if (interact === 1 && (!this.scene.isLocalPlayerDead() || this.scene.isPriestNpc(id))) {
          this.scene.cursor.setGameCursor(npcCursor);
        }
      });

      tileZone.on('pointerout', () => {
        const r = this.scene.npcObjects.get(id);
        if (r) r.nameHovered = false;
        if (interact === 1) this.scene.cursor.setGameCursor('main');
      });

      // Interactividad sobre el tile (no sobre el gráfico) para que sea más fácil clickear
      // Solo hacer interactivo si interact=1. useHandCursor:false → el cursor lo manejamos
      // nosotros por tipo de NPC (shop/enchant/talk) en pointerover/out.
      if (interact === 1) {
        tileZone.setInteractive({ useHandCursor: false });

        // Click IZQUIERDO: solo chat bubble local con el `message` cacheado.
        // Click DERECHO: handleNpcRightClick decide entre INTERACT_NPC al server
        // (vendor / priest) o abrir el NpcInfoPanel local. Phaser usa topOnly por
        // defecto, así que este tileZone (depth labelDepth+100) es quien recibe
        // el pointerdown — si no atendemos right-click acá, se pierde.
        tileZone.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
          if (pointer.rightButtonDown()) {
            this.scene.npcInfo.handleNpcRightClick(id);
            pointer.event.stopPropagation();
            return;
          }
          if (message) this.scene.overheadText.showNpcChatBubble(id, message);
        });
      } else {
        // NPC no interactivo: no necesita interactividad en el sprite
        // El hover del tile ya maneja el nombre
        sprite.setInteractive({ useHandCursor: false });
      }

      // All sprites are added directly to scene - Phaser handles depth sorting automatically

      const renderObj: NPCRenderObjects = {
        sprite,
        castShadow: this.scene.shadows.createCastShadowLayers(),
        shadow: shadowSprite,
        dropshadow: dropshadowSprite,
        shadowFade: 1,
        head: headSprite,
        shield: shieldSprite,
        weapon: weaponSprite,
        label,
        typeIcon,
        tileZone,
        nameHovered: false,
        hostile: npcTmpl?.hostile === true,
        bodyId,
        lastTileX: npc.x,
        lastTileY: npc.y,
        walkUntil: 0,
        // Sistema de interpolación basada en tiempo
        currentWorldX: worldX,
        currentWorldY: worldY,
        targetWorldX: worldX,
        targetWorldY: worldY,
        startWorldX: worldX,
        startWorldY: worldY,
        moveStartTime: 0,
        moveDuration: 0,
        moveSpeed: 0,
        breathingOffset: breathingPhaseOffset(id), // determinístico por id → sincronizado entre clientes
        previousHp: null,
        previousMana: null,
      };

      // Guardar interact y message en el sprite para referencia

      (sprite as any).npcInteract = interact;

      (sprite as any).npcMessage = message;

      // AO-like: Almacenar coordenadas de mundo para actualización en cada frame
      sprite.setData('worldX', worldX);
      sprite.setData('worldY', worldY);

      // Inicializar valores previos de HP/Mana con los valores actuales del NPC
      renderObj.previousHp = npc.minHp ?? 0;
      renderObj.previousMana = 0; // NPCs no tienen mana por ahora, pero mantener compatibilidad

      this.scene.npcObjects.set(id, renderObj);
      
      // Register NPC light source
      this.scene.entityLight.registerEntityLight(`npc_${id}`, worldX, worldY);
    } else {
      // Detectar ganancias de HP (NPCs no tienen mana por ahora)
      const currentHp = npc.minHp ?? 0;
      
      if (existing.previousHp !== null && currentHp > existing.previousHp) {
        // Ganancia de HP
        const hpGain = currentHp - existing.previousHp;
        this.scene.overheadText.showHealthManaGainText(id, hpGain, true, false);
        // Reproducir animación5
        const animKey = `animation5`;
        const animDef = this.scene.animationsIndex?.[animKey];
        const offsetY = animDef?.offsetY ?? 0;
        const x = existing.sprite.x;
        const y = existing.sprite.y + offsetY;
        const depth = existing.sprite.depth + 100;
        playAnimationAt(this.scene, 5, x, y, false, depth);
      }
      
      // Muerte del NPC: transición HP>0 → HP≤0 → SFX de muerte (default; override por template a
      // futuro). Posicional en el tile del NPC. Ver docs/SOUND_AND_MUSIC.md.
      if (existing.previousHp !== null && existing.previousHp > 0 && currentHp <= 0) {
        this.scene.audioWorld.playAtEntity(COMBAT_DEFAULTS.npcDeath, id);
      }

      // Actualizar valor previo
      existing.previousHp = currentHp;

      // Detectar si cambió de tile (movimiento)
      const positionChanged = existing.lastTileX !== npc.x || existing.lastTileY !== npc.y;
      
      if (positionChanged) {
        existing.lastTileX = npc.x;
        existing.lastTileY = npc.y;

        // TIME-BASED INTERPOLATION for NPCs (same as players).
        // Escalar la duración con el moveSpeed propio del NPC: un mob con
        // moveSpeed=0.9 se interpola más lento (~222ms), uno con moveSpeed=1.5
        // más rápido (~133ms). El server avanza el NPC a la misma cadencia que
        // esta interpolación, así no hay desync visual.
        existing.startWorldX = existing.currentWorldX;
        existing.startWorldY = existing.currentWorldY;
        existing.targetWorldX = worldX;
        existing.targetWorldY = worldY;
        existing.moveStartTime = this.scene.time.now;
        const npcSpeed = (npc as { moveSpeed?: number }).moveSpeed ?? 1.0;
        existing.moveDuration = npcSpeed > 0
          ? GameScene.REMOTE_STEP_MS / npcSpeed
          : GameScene.REMOTE_STEP_MS;
        existing.moveSpeed = GameScene.PLAYER_MOVE_SPEED;
      } else if (npc.state === EntityState.IDLE && existing.moveDuration <= 0) {
        // Idle y sin interpolación activa: snap al tile del servidor
        existing.currentWorldX = worldX;
        existing.currentWorldY = worldY;
        existing.targetWorldX = worldX;
        existing.targetWorldY = worldY;
        existing.startWorldX = worldX;
        existing.startWorldY = worldY;
        existing.moveDuration = 0;
        existing.moveSpeed = 0;
      }
      
      // Texto del nombre (puede cambiar en runtime). La POSICIÓN de label/head/shield/weapon NO se
      // setea acá: updateNPCsPositions() la reescribe este mismo frame desde currentWorldX/Y
      // (interpolada). El bloque viejo posicionaba con `screenX`/`screenY` —variables inexistentes en
      // esta función, que resolvían a los globals window.screenX/screenY— y se pisaba de inmediato:
      // era trabajo redundante por-NPC por-frame + bug latente (si cambiaba el orden de update, los
      // NPCs saltaban a la posición de la ventana en el monitor). Eliminado.
      existing.label.setText(npc.name || 'NPC');

      // Reaplicar visibilidad: siempre oculto, se muestra en hover sobre el tile
      existing.label.setAlpha(0);
      existing.label.setVisible(false);

      // Actualizar la posición del tileZone (los handlers de pointer ya se
      // wirean en la creación inicial del NPC, más arriba en esta función).
      if (existing.tileZone) {
        existing.tileZone.setPosition(worldX, worldY);
      }

      // Actualizar interactividad si cambió

      const currentInteract = (existing.sprite as any).npcInteract ?? 0;

      if (currentInteract !== interact) {
        // Limpiar eventos anteriores

        existing.sprite.removeAllListeners();

        existing.sprite.disableInteractive();

        if (interact === 1) {
          existing.sprite.setInteractive({ useHandCursor: true });

          existing.sprite.on('pointerover', () => {
            if (this.scene.npcNameVisibilityMode === 'hover') {
              existing.label.setAlpha(1);
            }
          });

          existing.sprite.on('pointerout', () => {
            if (this.scene.npcNameVisibilityMode === 'hover') {
              existing.label.setAlpha(0);
            }
          });

          // Left click: solo chat bubble local. Right click: acción según tipo
          // (vendor → INTERACT_NPC al server, hostile → NpcInfoPanel).
          existing.sprite.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
            if (pointer.rightButtonDown()) {
              this.scene.npcInfo.handleNpcRightClick(id);
              pointer.event.stopPropagation();
              return;
            }
            if (message) this.scene.overheadText.showNpcChatBubble(id, message);
          });
        } else {
          existing.sprite.setInteractive({ useHandCursor: false });

          existing.sprite.on('pointerover', () => {
            if (this.scene.npcNameVisibilityMode === 'hover') {
              existing.label.setAlpha(1);
            }
          });

          existing.sprite.on('pointerout', () => {
            if (this.scene.npcNameVisibilityMode === 'hover') {
              existing.label.setAlpha(0);
            }
          });
        }

        (existing.sprite as any).npcInteract = interact;
      }

      // Actualizar mensaje siempre

      (existing.sprite as any).npcMessage = message;
    }

    const render = this.scene.npcObjects.get(id);

    if (!render) return;

    // AUTO-REPARACIÓN por frame: cortar/animar el body si el warmup async aún no lo hizo (o se
    // abortó). Idempotente y O(1) cuando ya está listo. La rama idle re-setea el frame por tick,
    // así que sin esto un NPC parado con un body no-cortado spamea "no frame idx" sin parar y se
    // ve como la lámina entera. Ver ensureBodyReady.
    ensureBodyReady(this.scene, bodyId);

    const bodyAnimKey = getBodyAnimKey(bodyId, direction);

    const now = this.scene.time.now;

    // Detectar si cambió de tile este frame

    let movedThisFrame = false;

    if (render.lastTileX !== npc.x || render.lastTileY !== npc.y) {
      // Huella del NPC en el tile que dejó atrás (set según su template, default 1).
      this.scene.footsteps.spawnRemoteEntityFootstep(
        render.lastTileX,
        render.lastTileY,
        npc.x,
        npc.y,
        ((npc as unknown as { footstepId?: number }).footstepId ?? DEFAULT_PLAYER_FOOTSTEP_ID),
        (npc as unknown as { footstepDuration?: number }).footstepDuration ??
          FOOTSTEP_BASE_DURATION_MS,
        false,
        false,
        id
      );
      render.lastTileX = npc.x;

      render.lastTileY = npc.y;

      movedThisFrame = true;
    }

    // Determinar si debe animar caminata basándose en si se está moviendo
    const shouldAnimateWalk = movedThisFrame || 
      (render.currentWorldX !== render.targetWorldX || render.currentWorldY !== render.targetWorldY);

    if (shouldAnimateWalk) {
      // Tempo escalado por moveSpeed del NPC (como ApplySpeedingToChar de AO)
      const npcWalkTimeScale = ((npc as any).moveSpeed as number | undefined) || 1.0;
      render.sprite.anims.timeScale = npcWalkTimeScale;
      if (render.shadow) {
        render.shadow.anims.timeScale = npcWalkTimeScale;
      }

      // GUARD a prueba de balas (mismo caso que PlayerSyncController): la anim puede no
      // existir (warmup chunked) o existir pero estar VACÍA (0 frames) → play() crashea
      // leyendo currentFrame.duration y rompe el frame de update(). Fallback estático +
      // try/catch como red final; una anim rota NUNCA debe tumbar el loop de render.
      const bodyAnim = this.scene.anims.exists(bodyAnimKey) ? this.scene.anims.get(bodyAnimKey) : null;
      if (!bodyAnim || bodyAnim.frames.length === 0) {
        warnBadBodyAnim(bodyAnimKey, bodyId, bodyAnim ? 'sin-frames' : 'inexistente');
        render.sprite.anims.stop();
        render.sprite.setFrame(getBodyIdleFrame(direction, bodyId));
        if (render.shadow) {
          render.shadow.anims.stop();
          render.shadow.setFrame(getBodyIdleFrame(direction, bodyId));
        }
      } else if (
        render.sprite.anims.currentAnim?.key !== bodyAnimKey ||
        !render.sprite.anims.isPlaying
      ) {
        // Preservar fase al girar en movimiento (SyncGrhPhase de AO)
        let startFrame = 0;
        const prevKey = render.sprite.anims.currentAnim?.key;
        if (render.sprite.anims.isPlaying && prevKey && prevKey !== bodyAnimKey) {
          const idx = parseInt(render.sprite.frame.name, 10);
          if (Number.isFinite(idx)) {
            const lateral = direction === Direction.LEFT || direction === Direction.RIGHT;
            const bodyCols = getBodyColumns(bodyId);
            const walkLen = lateral ? getBodyLateralColumns(bodyId) : bodyCols;
            startFrame = (idx % bodyCols) % walkLen;
          }
        }
        try {
          render.sprite.anims.play({ key: bodyAnimKey, startFrame }, true);
          // Sync NPC shadow animation with body
          if (render.shadow) {
            render.shadow.anims.play({ key: bodyAnimKey, startFrame }, true);
          }
        } catch {
          warnBadBodyAnim(bodyAnimKey, bodyId, 'play-throw');
          render.sprite.anims.stop();
          render.sprite.setFrame(getBodyIdleFrame(direction, bodyId));
        }
      }
    } else {
      render.sprite.anims.stop();
      render.sprite.anims.timeScale = 1;

      render.sprite.setFrame(getBodyIdleFrame(direction, bodyId));

      // Sync NPC shadow frame with body (idle state)
      if (render.shadow) {
        render.shadow.anims.stop();
        render.shadow.anims.timeScale = 1;
        render.shadow.setFrame(getBodyIdleFrame(direction, bodyId));
      }
    }

    // Cabeza estática según dirección

    if (render.head) {
      render.head.setFrame(getHeadFrameForDirection(direction));
    }

    // Por ahora shield/weapon para NPCs usan solo frame idle por dirección

    if (render.shield) {
      render.shield.setFrame(getShieldIdleFrame(direction));
    }

    if (render.weapon) {
      render.weapon.setFrame(getWeaponIdleFrame(direction));
    }

    // Profundidades básicas para NPCs (similar a jugadores)

    const baseDepth = this.scene.getEntityBaseDepthFromTileY(npc.y);

    const headDelta = 0.01;

    const frontDelta = 0.02;

    const backDelta = -0.02;

    const labelDelta = 0.03;

    render.sprite.setDepth(baseDepth);

    if (render.head) {
      render.head.setDepth(baseDepth + headDelta);
    }

    if (render.weapon) {
      render.weapon.setDepth(baseDepth + frontDelta);
    }

    if (render.shield) {
      render.shield.setDepth(baseDepth + frontDelta);
    }

    render.label.setDepth(baseDepth + labelDelta);

    // Visibilidad por estado: si el NPC está muerto, el cuerpo desaparece hasta
    // el respawn. El loot ya cae al suelo como groundItems en tiles distintos.
    // La label no se toca aquí — su alpha lo maneja el sistema de hover.
    const isDead = npc.state === EntityState.DEAD;
    if (render.sprite.visible === isDead) {
      render.sprite.setVisible(!isDead);
      if (render.head) render.head.setVisible(!isDead);
      if (render.weapon) render.weapon.setVisible(!isDead);
      if (render.shield) render.shield.setVisible(!isDead);
      if (render.shadow) render.shadow.setVisible(!isDead);
      if (render.tileZone?.input) {
        render.tileZone.input.enabled = !isDead;
      }
    }

    // Sync de FX persistentes del NPC (mismo canal que players, F4). Hoy lo usa
    // la partícula de silencio. `getEntity` del ParticleManager ya resuelve NPCs.
    const npcAuraFxIds = (npc as unknown as { auraFxIds?: { length: number; [k: number]: string } }).auraFxIds;
    if (npcAuraFxIds && typeof npcAuraFxIds.length === 'number') {
      const ids: string[] = [];
      for (let i = 0; i < npcAuraFxIds.length; i++) ids.push(npcAuraFxIds[i]);
      this.scene.entityVfx.syncAuraEmittersFor(id, ids);
    }

    // Pérdida de movilidad: raíces (inmovilizado) / tint pétreo (paralizado).
    this.scene.entityVfx.syncImpairVisualsFor(id, (npc as unknown as { impairState?: number }).impairState ?? 0);
    // Invisibilidad (summons que hereden sigilo): cloak + alpha.
    this.scene.entityVfx.syncInvisibilityVisualsFor(id, (npc as unknown as { invisibleState?: number }).invisibleState ?? 0, false);
  }

  public applyHeadingArrowVisibility(): void {
    const visible = this.scene.showHeadingArrow;

    this.scene.playerObjects.forEach((render) => {
      render.arrow.setVisible(visible);
    });
  }

  public removeNPC(id: string): void {
    const existing = this.scene.npcObjects.get(id);

    if (!existing) return;

    existing.sprite.destroy();

    // Destroy cast shadow (silueta proyectada multi-capa)
    this.scene.shadows.destroyCastShadow(existing.castShadow);

    // Destroy shadow sprite
    existing.shadow?.destroy();

    // Destroy dropshadow sprite
    existing.dropshadow?.destroy();

    existing.head?.destroy();

    existing.shield?.destroy();

    existing.weapon?.destroy();

    this.scene.labels.destroyEntityLabel(existing.label);

    existing.typeIcon?.destroy();

    existing.tileZone?.destroy();

    // Remove NPC light source
    this.scene.entityLight.removeEntityLight(`npc_${id}`);

    // Parar cualquier FX persistente anclado al NPC (ej. partícula de silencio).
    this.scene.entityVfx.clearAuraEmittersFor(id);
    this.scene.entityVfx.clearImpairVisualsFor(id);
    this.scene.entityVfx.clearInvisibilityVisualsFor(id);
    // Protección de aparición: remover halo + tween si seguía activo (silent, sin destello)
    this.scene.stateVfx.stopSpawnProtectionVisual(id, true);

    this.scene.npcObjects.delete(id);
  }

}
